import { CommonModule, PercentPipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import {
    MatPaginator,
    MatPaginatorModule,
    PageEvent,
} from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FuseCardComponent } from '@fuse/components/card';
import { AnnouncementCard1 } from 'app/components/category-cards/category-cards.component';
import { CategoryDetailModalComponent } from 'app/components/category-modal/category-modal.component';
import { PermissionService } from 'app/components/permission-modal/permission.service';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import Swal from 'sweetalert2';
import { AdminCategoryMetricsDTO, AdminCategorySearchDTO } from './category';
import { CategoryService } from './category.service';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import * as phosphorIcons from '@ng-icons/phosphor-icons/regular';

@Component({
    selector: 'list-categories',
    templateUrl: './list-categories.component.html',
    styleUrls: ['./list-categories.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        MatTableModule,
        MatPaginatorModule,
        MatTooltipModule,
        FuseCardComponent,
        PercentPipe,
        MatInputModule,
        MatFormFieldModule,
        MatSelectModule,
        FormsModule,
        MatSortModule,
        AnnouncementCard1,
        DragDropModule,
        NgIconComponent
    ],
    providers: [CategoryService],
    viewProviders: [provideIcons(phosphorIcons)]
})
export class ListCategoriesComponent implements OnInit {
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;

    dataSource = new MatTableDataSource<AdminCategoryMetricsDTO>([]);
    displayedColumns: string[] = [
        'priority_order',
        'icon',
        // 'name',
        'subcategory',
        'activeAdsCount',
        'conversionRate',
        // 'trend',
        'status',
        'action',
    ];

    searchParams: AdminCategorySearchDTO = {
        q: '',
        status: undefined,
        page: 0,
        size: 100,
        sort: 'priority_order',
        direction: 'ASC',
    };
    totalAmount = 0;
    metrics: any;
    analyticalMetrics: any

    private _searchSubject = new Subject<string>();
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private dialog: MatDialog,
        private _categoryService: CategoryService,
        public permissionService: PermissionService,
    ) { }

    ngOnInit(): void {
        this.loadCategories();
        this.getMetrics();
        this.getAnaliticMetrics();

        this._searchSubject
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((query) => {
                this.searchParams.q = query;
                this.searchParams.page = 0;
                this.loadCategories();
            });
    }

    getAnaliticMetrics() {
        this._categoryService.listAnalyticalMetrics().subscribe((response) => {
            this.analyticalMetrics = response;
        });
    }

    getMetrics() {
        this._categoryService.getMetrics().subscribe((response) => {
            this.metrics = response;
        });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    loadCategories(): void {
        this._categoryService
            .search(this.searchParams)
            .subscribe((response) => {
                this.dataSource.data = response.list;
                this.totalAmount = response.totalAmount;
                this._changeDetectorRef.detectChanges();
            });
    }

    drop(event: CdkDragDrop<AdminCategoryMetricsDTO[]>) {
        if (event.previousIndex === event.currentIndex) {
            return;
        }

        const data = this.dataSource.data;
        moveItemInArray(data, event.previousIndex, event.currentIndex);

        data.forEach((category, index) => {
            category.priority_order = index + 1;
        });

        this.dataSource.data = [...data];

        const orders = data.map(c => ({ id: c.id!, priority_order: c.priority_order! }));
        this._categoryService.reorderCategories(orders).subscribe(() => {
            // Reordered successfully
        });
    }

    newCategory(row?: any) {
        this.dialog
            .open(CategoryDetailModalComponent, {
                data: row,
            })
            .afterClosed()
            .subscribe((result) => {
                this.loadCategories();
            });
    }

    blockCategory(row: any): void {
        Swal.fire({
            title: 'Inativar categoria?',
            text: 'Tem certeza que deseja inativar esta categoria?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sim, inativar',
            cancelButtonText: 'Cancelar',
        }).then((result) => {
            if (result.isConfirmed) {
                this._categoryService
                    .updateStatus(row.id, 'BLOCKED')
                    .subscribe((resp) => {
                        Swal.fire({
                            icon: 'success',
                            title: 'Categoria inativada com sucesso!',
                            showConfirmButton: false,
                            timer: 1500,
                        });
                        this.loadCategories();
                        this._changeDetectorRef.detectChanges();
                    });
            }
        });
    }

    reactivateCategory(row: any): void {
        Swal.fire({
            title: 'Ativar categoria?',
            text: 'Tem certeza que deseja ativar esta categoria?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sim, ativar',
            cancelButtonText: 'Cancelar',
        }).then((result) => {
            if (result.isConfirmed) {
                this._categoryService
                    .updateStatus(row.id, 'ACTIVE')
                    .subscribe((resp) => {
                        Swal.fire({
                            icon: 'success',
                            title: 'Categoria ativada com sucesso!',
                            showConfirmButton: false,
                            timer: 1500,
                        });
                        this.loadCategories();
                        this._changeDetectorRef.detectChanges();
                    });
            }
        });
    }

    onSearch(query: string): void {
        this._searchSubject.next(query);
    }

    onStatusChange(status: any): void {
        this.searchParams.status = status === 'ALL' ? undefined : status;
        this.searchParams.page = 0;
        this.loadCategories();
    }

    handlePageEvent(e: PageEvent): void {
        this.searchParams.page = e.pageIndex;
        this.searchParams.size = e.pageSize;
        this.loadCategories();
    }

    onSortChange(sort: Sort): void {
        this.searchParams.sort = sort.active;
        this.searchParams.direction =
            (sort.direction.toUpperCase() as 'ASC' | 'DESC') || 'ASC';
        this.searchParams.page = 0;
        this.loadCategories();
    }

    loadMetrics(): void {
        this.loadCategories();
    }

    toggleStatus(category: AdminCategoryMetricsDTO): void {
        const newStatus = category.status === 'ACTIVE' ? 'BLOCKED' : 'ACTIVE';
        const actionText = newStatus === 'ACTIVE' ? 'Ativar' : 'Inativar';

        Swal.fire({
            title: `${actionText} Categoria`,
            text: `Tem certeza que deseja ${actionText.toLowerCase()} a categoria "${category.name}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: `Sim, ${actionText.toLowerCase()}`,
            cancelButtonText: 'Cancelar',
        }).then((result) => {
            if (result.isConfirmed) {
                this._categoryService
                    .updateStatus(category.id, newStatus)
                    .subscribe(() => {
                        Swal.fire({
                            icon: 'success',
                            title: `Categoria ${newStatus === 'ACTIVE' ? 'ativada' : 'inativada'} com sucesso!`,
                            showConfirmButton: false,
                            timer: 1500,
                        });
                        this.loadMetrics();
                    });
            }
        });
    }

    editCategory(category: AdminCategoryMetricsDTO): void {
        console.log('Edit category:', category);
    }
}
